import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFormChecklist, patchFormChecklist, deleteFormChecklist } from '../formChecklistsService';

const lsGet = (key) => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        localStorage.removeItem(key);
        return null;
    }
};

const lsSet = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        localStorage.removeItem(key);
    }
};

export function useFormChecklistDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [checklist, setChecklist] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [checked, setChecked] = useState({});
    const [checkedNoGo, setCheckedNoGo] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const buildCheckedState = useCallback((cl) => {
        const initial = {};
        if (cl?.steps) {
            cl.steps.forEach(step => {
                step.tasks.forEach(task => {
                    initial[task.id] = false;
                });
            });
        }
        return initial;
    }, []);

    const loadChecklist = useCallback(async () => {
        setLoaded(false);
        setError(null);
        try {
            const data = await getFormChecklist(id);
            setChecklist(data);
            const savedGo = lsGet(`fc-${id}-go`);
            const savedNoGo = lsGet(`fc-${id}-nogo`);
            setChecked(savedGo ?? buildCheckedState(data));
            setCheckedNoGo(savedNoGo ?? buildCheckedState(data));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoaded(true);
        }
    }, [id, buildCheckedState]);

    useEffect(() => {
        loadChecklist();
    }, [loadChecklist]);

    const handleCheck = (taskId, type) => {
        const nextGo = { ...checked, [taskId]: type === 'go' ? !checked[taskId] : false };
        const nextNoGo = { ...checkedNoGo, [taskId]: type === 'nogo' ? !checkedNoGo[taskId] : false };
        setChecked(nextGo);
        setCheckedNoGo(nextNoGo);
        lsSet(`fc-${id}-go`, nextGo);
        lsSet(`fc-${id}-nogo`, nextNoGo);
    };

    const handleClearForm = () => {
        const resetGo = Object.fromEntries(Object.keys(checked).map(k => [k, false]));
        const resetNoGo = Object.fromEntries(Object.keys(checkedNoGo).map(k => [k, false]));
        setChecked(resetGo);
        setCheckedNoGo(resetNoGo);
        lsSet(`fc-${id}-go`, resetGo);
        lsSet(`fc-${id}-nogo`, resetNoGo);
    };

    const handleExport = () => {
        window.print();
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this checklist?')) return;
        try {
            await deleteFormChecklist(id);
            navigate('/documents/form-checklists');
        } catch (err) {
            setSaveError(err.message);
        }
    };

    return {
        id,
        checklist,
        loaded,
        error,
        clearError: () => setError(null),
        checked,
        checkedNoGo,
        saving,
        saveError,
        setSaveError,
        handleCheck,
        handleClearForm,
        handleExport,
        handleDelete,
    };
}
